import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO20000_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO/IEC 20000-1:2018 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            IT Service Management (ITSM) — International standard for delivering
            managed IT services with quality, efficiency, and continual
            improvement.
          </p>
        </motion.div>
      </section>

      {/* Overview Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO 20000-1 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              {`ISO 20000-1 is the international standard for Information Technology Service Management (ITSM), providing a framework for organizations to deliver high-quality IT services to their customers. The standard helps businesses establish an effective IT service management system (SMS), ensuring that their IT services meet the needs of customers and comply with business objectives.`}
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              ISO 20000-1 certification is crucial for organizations aiming to
              improve their service delivery, ensure service continuity, and
              enhance customer satisfaction through consistent IT performance.
              By implementing ISO 20000-1, businesses can ensure they have the
              right processes, roles, and responsibilities in place to manage IT
              services effectively and align with best practices in ITSM.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO 20000-1 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 20000-1 certification is a recognition that an organization
              has successfully implemented an IT service management system (SMS)
              that meets the requirements of the ISO 20000-1 standard. The
              certification helps businesses ensure that their IT services are
              delivered efficiently, effectively, and in a way that aligns with
              customer expectations and business goals.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              The standard is based on best practices in ITSM and covers key
              areas such as:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Service delivery management",
                "Service design and transition",
                "Service operation",
                "Service improvement",
                "Risk management",
                "Business continuity",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO 20000-1 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Service Management System (SMS)",
                desc: "ISO 20000-1 provides a comprehensive framework for establishing, implementing, and maintaining a service management system that aligns with industry best practices and customer needs.",
              },
              {
                icon: BarChart3,
                title: "Service Design and Delivery",
                desc: "The standard helps businesses ensure that IT services are designed, developed, and delivered in line with customer requirements and business objectives, promoting consistency and reliability.",
              },
              {
                icon: Users,
                title: "Service Improvement",
                desc: "Focuses on continuous improvement through regular monitoring, performance evaluation, and feedback loops, helping businesses enhance the quality of IT services and increase customer satisfaction.",
              },
              {
                icon: Award,
                title: "Risk Management",
                desc: "Ensures that IT risks are identified, assessed, and mitigated, reducing the likelihood of service disruptions and ensuring business continuity.",
              },
              {
                icon: Globe,
                title: "Alignment with Business Objectives",
                desc: "The certification ensures that IT services are aligned with the organization’s strategic goals and objectives, supporting operational efficiency and improving service delivery.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO 20000-1 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Improved Service Delivery — ISO 20000-1 helps businesses deliver high-quality IT services consistently, improving service availability, responsiveness, and customer satisfaction.",
                "Risk Mitigation — Implementing an IT service management system ensures that potential IT risks (e.g., service disruptions, security breaches) are proactively identified and managed, reducing downtime and improving service continuity.",
                "Regulatory Compliance — Achieving ISO 20000-1 certification helps businesses comply with ITSM standards, data protection regulations, and industry best practices, reducing the risk of non-compliance and penalties.",
                "Customer Trust and Satisfaction — ISO 20000-1 certification demonstrates your commitment to high-quality IT service delivery, increasing trust among customers and partners and improving customer retention.",
                "Operational Efficiency — By aligning IT services with business goals and improving service delivery processes, businesses can achieve greater efficiency, cost savings, and enhanced overall business performance.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO 20000-1 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO 20000-1 certification encompasses the following key
              components:
            </p>
            <div className="space-y-4">
              {[
                "1. Service Management System (SMS) Requirements — The certification requires organizations to establish an IT service management system (SMS) that covers all aspects of IT service delivery, including policy, governance, and process management.",
                "2. Service Design and Transition — The standard provides guidelines for service design, ensuring that services meet customer needs and business objectives while facilitating seamless service transitions.",
                "3. Service Operation — Focuses on service operation and incident management, ensuring that services are delivered effectively and efficiently and that issues are resolved promptly.",
                "4. Service Monitoring and Measurement — ISO 20000-1 requires organizations to monitor and measure service performance, using key performance indicators (KPIs) and metrics to assess service quality and identify areas for improvement.",
                "5. Continual Improvement — Emphasizes continuous improvement (using tools like PDCA—Plan-Do-Check-Act) to ensure that services evolve to meet changing customer expectations, regulatory requirements, and business needs.",
                "6. Business Continuity Management — The standard helps businesses design and implement business continuity plans to ensure that IT services are resilient and can recover quickly from disruptions.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO 20000-1 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Enhanced IT Service Quality",
                "Operational Cost Savings",
                "Improved Risk Management",
                "Increased Customer Satisfaction",
                "Competitive Advantage",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO 20000-1 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The process for obtaining ISO 20000-1 certification typically
              includes the following steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Service Management System (SMS) Assessment — Conduct an internal gap analysis to assess your current service management practices and identify areas for improvement.",
                "2. Define SMS Scope and Objectives — Establish the scope and objectives of your IT service management system, ensuring alignment with business goals and customer requirements.",
                "3. Develop and Implement Processes — Develop and implement the necessary processes and procedures for service delivery, risk management, incident management, and continual improvement.",
                "4. Internal Audit — Perform an internal audit to assess the effectiveness of your SMS and ensure that it meets the requirements of the ISO 20000-1 standard.",
                "5. Third-Party Certification Audit — Engage an accredited third-party certification body to conduct an audit of your IT service management system and verify compliance with ISO 20000-1.",
                "6. Certification and Continuous Improvement — Once certified, maintain ISO 20000-1 compliance through regular monitoring, audits, and continual improvement initiatives.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO 20000-1 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides expert ISO 20000-1 certification
              consultancy services, guiding businesses through the process of
              establishing, implementing, and continuously improving their IT
              service management systems (SMS).
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "ISO 20000-1 Self-Assessment and Gap Analysis",
                "SMS Strategy and Process Development",
                "Service Delivery Optimization",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Ongoing Monitoring and Continuous Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO 20000-1 certification, ensuring
              consistent IT service delivery, customer satisfaction, and
              compliance with best practices in IT service management.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO20000_Details;
